import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { bikeQueries } from "../../bikes/api/bikes.queries";
import { documentTypesQueries } from "../../fsm-admin/document-types/api/document-types.queries";
import { locationQueries } from "../../locations/api/locations.queries";
import { requestsQueries } from "../../requests/api/requests.queries";
import { authQueries } from "../api/auth.queries";
import type {
  LocationStat,
  WelcomeRequest,
} from "../components/WelcomeDashboard";
import { deriveVariant } from "../utils/deriveVariant";

export function useWelcomePage(userId?: string, customerId?: string) {
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    ...authQueries.roles(userId ?? ""),
    enabled: !!userId,
  });

  const { data: jwtPayload } = useQuery(authQueries.me());

  const { data: allLocations = [], isLoading: loadingLocations } = useQuery(
    locationQueries.picker({ customerId }),
  );

  const { data: requestsPage, isLoading: loadingRequests } = useQuery(
    requestsQueries.list({ page: 1, pageSize: 5, customerId }),
  );

  const { data: allBikes = [] } = useQuery(bikeQueries.picker({ customerId }));

  const { data: activeRequestsPage } = useQuery(
    requestsQueries.list({
      page: 1,
      pageSize: 500,
      status: "active",
      customerId,
    }),
  );

  const { data: userDocumentTypes = [] } = useQuery(
    documentTypesQueries.forUser(customerId),
  );

  const variant = useMemo(
    () => deriveVariant(rolesData?.roles ?? [], jwtPayload?.role),
    [rolesData, jwtPayload],
  );

  const locations = useMemo((): LocationStat[] => {
    const activeRequests = activeRequestsPage?.data ?? [];
    return allLocations.map((loc) => {
      const locBikes = allBikes.filter((b) => b.locationId === loc.id);
      const inRepair = locBikes.filter(
        (b) => b.status === "in_repair" || b.status === "en_garantia",
      ).length;
      const openCount = activeRequests.filter(
        (r) => r.locationId === loc.id,
      ).length;
      return {
        id: loc.id,
        name: loc.name,
        city: loc.city,
        stats: [
          { label: "Open requests", value: openCount },
          { label: "Bikes total", value: locBikes.length },
          {
            label: "In repair",
            value: inRepair,
            highlight: inRepair > 0 ? ("warning" as const) : undefined,
          },
        ],
      };
    });
  }, [allLocations, allBikes, activeRequestsPage]);

  const primaryLocation =
    locations.length > 0 ? locations.map((l) => l.name).join(", ") : undefined;

  const recentRequests = useMemo(
    (): WelcomeRequest[] =>
      (requestsPage?.data ?? []).map((r) => ({
        id: r.id,
        referenceNo: r.referenceNo,
        documentTypeName: r.documentTypeName,
        documentTypeTranslations: r.documentTypeTranslations,
        currentState: {
          name: r.currentState.name,
          isFinal: r.currentState.isFinal,
          translations: r.currentState.translations,
        },
        updatedAt: r.updatedAt,
        isActionable: r.isActionable ?? false,
      })),
    [requestsPage],
  );

  return {
    variant,
    locations,
    primaryLocation,
    jwtPayload,
    loadingRoles,
    loadingLocations,
    recentRequests,
    loadingRequests,
    userDocumentTypes,
  };
}
